/**
 * ArbitrationHeader Component
 *
 * «پروندهٔ اختلاف» — compact dispute detail card for the arbitration sidebar.
 * Designed to fit without scrolling: tight key-value rows, inline financial
 * strip, horizontal mini timeline. Theme-aware (alpha tints) for dark + light.
 *
 * `embedded` mode drops the outer card chrome (used inside the sidebar tabs).
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import StoreIcon from '@mui/icons-material/Store';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { IDisputeChat } from '../../services/arbitrationService';
import { TYPE_HEX } from './constants';

interface ArbitrationHeaderProps {
  dispute: IDisputeChat;
  onBack?: () => void;
  onRefresh?: () => void;
  loading?: boolean;
  /** بدون قاب کارت — برای استفاده داخل تب سایدبار */
  embedded?: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getDuration = (createdAt: string): string => {
  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return '—';
  const diffMs = Date.now() - created.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (diffDays > 0) return `${diffDays} روز و ${diffHours} ساعت`;
  return `${diffHours} ساعت`;
};

/** ردیف فشردهٔ «برچسب: مقدار» */
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, label, children, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', '& svg': { fontSize: 15 } }}>{icon}</Box>
      <Typography variant="caption" sx={{ fontSize: 11.5 }}>{label}</Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        fontSize: '0.8rem',
        fontWeight: 700,
        color: 'text.primary',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </Box>
    {action}
  </Box>
);

/** تایم‌لاین افقی سه‌مرحله‌ای — جای کمی می‌گیرد */
const HorizontalTimeline: React.FC<{ dispute: IDisputeChat; typeHex: string }> = ({ dispute, typeHex }) => {
  const steps = [
    { label: 'ثبت', date: dispute.createdAt, done: true, color: typeHex },
    { label: 'تأیید', date: dispute.approvedAt, done: !!dispute.approvedAt, color: typeHex },
    { label: 'حل پرونده', date: dispute.resolvedAt, done: !!dispute.resolvedAt, color: '#10b981' }
  ];

  return (
    <Box>
      {/* dots + connectors */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: s.done ? s.color : 'transparent',
                border: `2.5px solid ${s.done ? s.color : alpha(s.color, 0.3)}`
              }}
            />
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2.5,
                  borderRadius: 2,
                  mx: 0.5,
                  bgcolor: steps[i + 1].done ? alpha(steps[i + 1].color, 0.55) : alpha(typeHex, 0.15)
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
      {/* labels */}
      <Box sx={{ display: 'flex', mt: 0.6 }}>
        {steps.map((s, i) => (
          <Box
            key={s.label}
            sx={{
              flex: 1,
              textAlign: i === 0 ? 'right' : i === steps.length - 1 ? 'left' : 'center',
              px: 0.25,
              minWidth: 0
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 700, fontSize: 10.5, color: s.done ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}
            >
              {s.label}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 9, color: 'text.secondary', lineHeight: 1.4 }}>
              {s.done ? formatDate(s.date) : '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ArbitrationHeader: React.FC<ArbitrationHeaderProps> = ({ dispute, embedded = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const typeHex = TYPE_HEX[dispute.type] || '#0ea5e9';
  // بعضی اختلاف‌ها reference ندارند — گارد تا کل کارت نشکند
  const ref: Partial<IDisputeChat['reference']> = dispute.reference || {};

  const body = (
    <Box sx={{ p: 1.5 }}>
      {/* Header row (فقط حالت غیرembedded — در تب، خودِ تب عنوان است) */}
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <DescriptionIcon sx={{ fontSize: 16, color: typeHex }} />
          <Typography variant="subtitle2" fontWeight={800}>
            پروندهٔ اختلاف
          </Typography>
        </Box>
      )}

      {/* chips: مدت زمان + تعداد پیام */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: dispute.description ? 1 : 1.25 }}>
        <Chip
          size="small"
          icon={<AccessTimeIcon sx={{ fontSize: '13px !important' }} />}
          label={getDuration(dispute.createdAt)}
          sx={{
            height: 21,
            bgcolor: alpha(typeHex, isDark ? 0.22 : 0.1),
            color: typeHex,
            fontWeight: 700,
            '& .MuiChip-icon': { color: typeHex },
            '& .MuiChip-label': { px: 0.75, fontSize: 10.5 }
          }}
        />
        <Chip
          size="small"
          icon={<ChatIcon sx={{ fontSize: '12px !important' }} />}
          label={`${dispute.totalMessages} پیام`}
          variant="outlined"
          sx={{ height: 21, '& .MuiChip-icon': { color: 'text.secondary' }, '& .MuiChip-label': { px: 0.75, fontSize: 10.5 } }}
        />
      </Box>

      {/* description — حداکثر ۳ خط */}
      {dispute.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          title={dispute.description}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.7,
            mb: 1.25
          }}
        >
          {dispute.description}
        </Typography>
      )}

      {/* Reference rows */}
      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          px: 1.25,
          py: 0.25,
          mb: 1.25,
          '& > div + div': { borderTop: `1px dashed ${theme.palette.divider}` }
        }}
      >
        {ref.orderNumber && (
          <InfoRow
            icon={<ShoppingCartIcon />}
            label="سفارش"
            action={
              <Tooltip title="مشاهده سفارش">
                <IconButton size="small" onClick={() => window.open(`/orders?orderId=${ref.orderId}`, '_blank')}>
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            }
          >
            <Box component="span" sx={{ direction: 'ltr' }}>#{ref.orderNumber}</Box>
          </InfoRow>
        )}

        {ref.productTitle && (
          <InfoRow icon={<InventoryIcon />} label="محصول">
            <Tooltip title={ref.productTitle}>
              <span>{ref.productTitle}</span>
            </Tooltip>
          </InfoRow>
        )}

        {ref.storeName && (
          <InfoRow icon={<StoreIcon />} label="فروشگاه">
            {ref.storeName}
          </InfoRow>
        )}

        {!ref.orderNumber && !ref.productTitle && !ref.storeName && (
          <InfoRow icon={<DescriptionIcon />} label="مرجع">
            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>—</Box>
          </InfoRow>
        )}
      </Box>

      {/* واحدهای در اختلاف (چندتایی) */}
      {ref.totalQuantity && ref.totalQuantity > 1 && (
        <Typography variant="caption" color="error.main" fontWeight={700} sx={{ display: 'block', mb: 1.25, mt: -0.5 }}>
          واحد در اختلاف: {ref.disputedQuantity || 1} از {ref.totalQuantity}
          {ref.unitPrice ? ` · هر واحد ${ref.unitPrice.toLocaleString()} ت` : ''}
        </Typography>
      )}

      {/* Financial — نوار فشرده */}
      {dispute.financial && (
        <Box
          sx={{
            mb: 1.25,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha('#0ea5e9', isDark ? 0.12 : 0.06),
            border: `1px solid ${alpha('#0ea5e9', isDark ? 0.35 : 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <PaymentsIcon sx={{ fontSize: 17, color: '#0ea5e9', flexShrink: 0 }} />
          <Box sx={{ flex: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9.5, lineHeight: 1.3 }}>
                مبلغ
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 12 }}>
                {dispute.financial.amount.toLocaleString()}
              </Typography>
            </Box>
            {(dispute.financial.shippingCost || 0) > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9.5, lineHeight: 1.3 }}>
                  ارسال
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 12 }}>
                  {dispute.financial.shippingCost?.toLocaleString()}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9.5, lineHeight: 1.3 }}>
                قابل بازپرداخت
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 12, color: '#0ea5e9' }}>
                {dispute.financial.refundableAmount.toLocaleString()} {dispute.financial.currency}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Dispute reason */}
      {dispute.disputeReason && (
        <Box
          sx={{
            mb: 1.25,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha('#f59e0b', isDark ? 0.12 : 0.07),
            border: `1px solid ${alpha('#f59e0b', isDark ? 0.35 : 0.22)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
            <ReportProblemIcon sx={{ fontSize: 14, color: '#d97706' }} />
            <Typography variant="caption" fontWeight={800} sx={{ color: '#d97706', fontSize: 11 }}>
              دلیل اختلاف
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.7, fontSize: 11.5 }}>
            {dispute.disputeReason}
          </Typography>
          {dispute.disputeDescription && dispute.disputeDescription !== dispute.disputeReason && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.7, fontSize: 11 }}>
              {dispute.disputeDescription}
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.25 }} />

      {/* Horizontal timeline */}
      <HorizontalTimeline dispute={dispute} typeHex={typeHex} />
    </Box>
  );

  if (embedded) {
    return body;
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Box sx={{ height: 4, bgcolor: typeHex }} />
      {body}
    </Paper>
  );
};

export default ArbitrationHeader;
